import { Fragment } from "react";
import { connect } from "react-redux";
import { History } from "history";

import {
  updateWidgetPropsSaga,
  updateWidgetOrderSaga,
} from "src/redux/actions";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { setGoogleAnalyticsEvent, toSpinalCase } from "src/common";

import { SideMenuItemI } from "src/types/SideMenuItem";
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  List,
  ListItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
} from "@chakra-ui/react";
import { SSOLink } from "./SSOLink";
import { getAsideMenuLinkClass, WidgetLink } from "./WidgetLink";
import { ToolLink } from "./ToolLink";
import { useLightMode } from "src/common/hooks/lightMode";
import { WidgetI } from "src/types/Widget";
import ReOrderIcon from "src/icons/ReOrderIcon";
import { useTranslate } from "@tolgee/react";

/**
 * Dynamically generated menu containing My Apps, Tools and Widgets along with edit option for each category
 */

interface SideSubMenuI {
  history?: History;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  impersonation?: any;
  isCondensed: boolean;
  isSmall: boolean;
  isSSO?: boolean;
  isWidget?: boolean;
  isWidgetVisible?: boolean;
  items: SideMenuItemI[] | WidgetI[];
  label: string;
  paramId?: string;
  path?: string;
  updateWidgetOrderSaga: (oldIndex: number, newIndex: number) => void;
  updateWidgetPropsSaga: (index: number, props: any) => void;
  widgetEditMode?: boolean;
  widgets?: WidgetI[];
}

const SideSubMenu = ({
  icon: LeftIcon,
  history,
  impersonation,
  isSmall,
  isSSO,
  isWidget,
  isWidgetVisible,
  items,
  label,
  paramId,
  path,
  updateWidgetOrderSaga,
  updateWidgetPropsSaga,
  widgetEditMode,
  widgets,
}: SideSubMenuI) => {
  const { lightMode } = useLightMode();
  const { t } = useTranslate();

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    updateWidgetOrderSaga(oldIndex, newIndex);
  };

  const handleToggle = (id) => {
    const index = widgets.findIndex((widget) => widget.id === id);
    const props = { ...widgets[index].properties };
    props.isVisible = !props.isVisible;
    setGoogleAnalyticsEvent({
      action: "portal_dashboard_widget_toggle_visibility",
      category: widgets[index].identifier,
      label: props.isVisible.toString(),
    });
    updateWidgetPropsSaga(index, props);
  };

  const handleSwitchKeyDown = (e) => {
    // Stop Space key events from propagating up to the draggable container
    if (e.key === " " || e.key === "Spacebar") {
      e.stopPropagation();
    }
  };

  const renderLink = (item, inPopover) => {
    const href = isWidget ? `#widget${item.id}` : item.href || "#";
    // We want to render absolute paths and specific relative file links as <a> tags
    const isAbsoluteOrFile =
      href.indexOf("http") === 0 || href.indexOf(".pdf") >= 0;

    if (isSSO) {
      return (
        <SSOLink
          history={history}
          impersonation={impersonation}
          item={item}
          isChild={true}
          inPopover={inPopover}
        />
      );
    }

    if (isAbsoluteOrFile) {
      return (
        <a
          href={item.href || "#"}
          target={item.target ? item.target : "_blank"}
          rel="noreferrer"
          data-aut={`Aside|AbsoluteLink|${item.label}`}
          onClick={() =>
            setGoogleAnalyticsEvent({
              action:
                item.type === "Link"
                  ? "portal_dashboard_link_click"
                  : "portal_dashboard_document_click",
            })
          }
          className={getAsideMenuLinkClass({ inPopover, isChild: true })}
        >
          <Flex>
            <span>{item.label}</span>
          </Flex>
        </a>
      );
    } else {
      if (isWidget) {
        // If this is a widget in edit mode, we don't want to render a link
        if (widgetEditMode) {
          const itemLabel = item.hasCustomName
            ? item.name
            : t({
                key: item.translatorDefaultNameKey,
                defaultValue: item.name,
              });
          return (
            <Fragment>
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="225px"
              >
                <FormLabel htmlFor={item.id} fontSize="16px">
                  {itemLabel}
                </FormLabel>
                <Switch
                  isChecked={item.properties.isVisible}
                  onChange={() => {
                    handleToggle(item.id);
                  }}
                  onKeyDown={handleSwitchKeyDown}
                  colorScheme="blue"
                  id={item.id}
                  sx={{
                    "span.chakra-switch__track[data-checked]": {
                      bg: "#387BB5",
                    },
                  }}
                />
              </FormControl>
            </Fragment>
          );
        }
        return <WidgetLink isChild={true} inPopover={inPopover} item={item} />;
      }
      return (
        <ToolLink
          href={`${item.href}?id=${item.id}`}
          isChild={true}
          inPopover={inPopover}
          item={item}
          paramId={paramId}
          path={path}
        />
      );
    }
  };

  const renderItem = (item, index, inPopover) => {
    const key = `${item.id || ""}${index}`;
    const MenuItem = ({ item, draggableListeners }) => {
      return (
        <ListItem
          key={key}
          paddingLeft={draggableListeners ? "12px" : undefined}
          paddingY={draggableListeners ? "8px" : undefined}
        >
          <Flex>
            {draggableListeners && (
              <IconButton
                aria-label={t({
                  key: "dashboard.sorting.reorderWidget",
                  defaultValue: "Reorder Widget",
                })}
                variant={"ghost"}
                size="sm"
                minH="24px"
                maxH={"24px"}
                minW="24px"
                maxW={"24px"}
                marginRight="8px"
                {...draggableListeners}
                icon={<ReOrderIcon />}
              />
            )}
            {renderLink(item, inPopover)}
          </Flex>
        </ListItem>
      );
    };

    // If we are in edit mode and this is a widget menu
    // menu items need to be sortable items so that they can be reordered
    if (widgetEditMode && isWidget) {
      const SortableItem = ({ item }) => {
        const { attributes, listeners, setNodeRef, transform, transition } =
          useSortable({ id: item.id, transition: null });
        const style = {
          transform: CSS.Transform.toString(transform),
          transition,
        };

        return (
          <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            tabIndex={null}
            role=""
          >
            <MenuItem item={item} draggableListeners={listeners} />
          </div>
        );
      };

      return <SortableItem item={item} key={key} />;
    }
    return (
      <MenuItem
        item={item}
        key={item.id || item.identifier}
        draggableListeners={null}
      />
    );
  };

  const renderItems = (inPopover?: boolean) => {
    const mappedItems = items.map((item, index) =>
      renderItem(item, index, inPopover),
    );
    // If this menu is not for widgets or if we are not in widgetedit mode, render items as unsortable
    if (!isWidget || !widgetEditMode) {
      return mappedItems;
    }
    // Otherwise render items as sortable
    const SortableContainer = ({ children }) => {
      const sortableItems = items.map((i) => i.id);
      const getPosition = (id) => sortableItems.indexOf(id) + 1; // prefer position over index
      const getItemName = (id) =>
        (items as WidgetI[]).find((i) => i.id === id)?.name;
      const itemCount = sortableItems.length;
      const announcements = {
        onDragStart({ active }) {
          return t({
            key: "dashboard.sorting.pickup",
            params: {
              name: getItemName(active.id),
              count: itemCount,
              position: getPosition(active.id),
            },
            defaultValue:
              "Pickup up sortable item {name}. Sortable item {name} is in position {position} of {count}",
          });
        },
        onDragOver({ active, over }) {
          if (over) {
            return t({
              key: "dashboard.sorting.moved",
              params: {
                name: getItemName(active.id),
                count: itemCount,
                position: getPosition(over.id),
              },
              defaultValue:
                "Sortable item {name} was moved into position {position} of {count}",
            });
          }
        },
        onDragEnd({ active, over }) {
          if (over) {
            return t({
              key: "dashboard.sorting.dropped",
              params: {
                name: getItemName(active.id),
                count: itemCount,
                position: getPosition(over.id),
              },
              defaultValue:
                "Sortable item {name} was dropped into position {position} of {count}",
            });
          }
        },
        onDragCancel({ active }) {
          return t({
            key: "dashboard.sorting.cancel",
            params: {
              name: getItemName(active.id),
            },
            defaultValue:
              "Dragging was cancelled. Sortable item {name} was dropped.",
          });
        },
      };

      const sensors = useSensors(
        useSensor(PointerSensor, {
          activationConstraint: {
            delay: 100,
            tolerance: 100,
          },
        }),
        useSensor(KeyboardSensor, {
          coordinateGetter: sortableKeyboardCoordinates,
          keyboardCodes: {
            start: ["Space", "Enter"],
            cancel: ["Escape"],
            end: ["Space", "Enter"],
          },
        }),
      );

      const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
          const oldIndex = sortableItems.indexOf(active.id);
          const newIndex = sortableItems.indexOf(over?.id || "");
          setGoogleAnalyticsEvent({
            action: "portal_dashboard_widget_reorder",
            category: items[oldIndex].identifier,
            label: newIndex.toString(),
          });
          arrayMove(items as any, oldIndex, newIndex);
          onSortEnd({ oldIndex, newIndex });
        }
      };

      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          accessibility={{ announcements }}
        >
          <SortableContext items={sortableItems}>{children}</SortableContext>
        </DndContext>
      );
    };

    return (
      <SortableContainer>
        <div className="sortable-widgets">{mappedItems}</div>
      </SortableContainer>
    );
  };

  if (isWidget && !isWidgetVisible) return null;

  const content = (
    <>
      {isSmall ? (
        <Popover placement="right-start">
          {({ isOpen }) => (
            <>
              <PopoverTrigger>
                <ListItem
                  title={label}
                  aria-label={label}
                  data-automation={toSpinalCase(label)}
                  marginY={"4px"}
                >
                  <button
                    className={`unstyled-button asidemenu-link small${isOpen ? " active" : ""}`}
                  >
                    <Flex width="100%" justify="center">
                      <LeftIcon
                        fontSize="22px"
                        aria-hidden
                        color={
                          lightMode
                            ? "var(--chakra-colors-gray-500)"
                            : "#C7C7C7"
                        }
                      />
                    </Flex>
                  </button>
                </ListItem>
              </PopoverTrigger>
              <PopoverContent
                ml={"8px"}
                backgroundColor={
                  lightMode ? "white" : "var(--headerFooterColor)"
                }
                border="none"
                borderTopLeftRadius={0}
                borderBottomLeftRadius={0}
              >
                <List>
                  <ListItem padding="8px 12px">{label}</ListItem>
                  {renderItems(true)}
                </List>
              </PopoverContent>
            </>
          )}
        </Popover>
      ) : (
        <>
          <ListItem marginY="8px" data-automation={toSpinalCase(label)}>
            <AccordionButton
              className="asidemenu-link"
              _expanded={{
                backgroundColor: lightMode ? "#EDEDED" : "#002533",
              }}
            >
              <Flex
                justifyContent={"space-between"}
                width={"100%"}
                alignItems={"center"}
              >
                <Flex>
                  <Flex mr="12px" alignItems={"center"}>
                    <LeftIcon
                      fontSize="22px"
                      color={
                        lightMode ? "var(--chakra-colors-gray-500)" : "#C7C7C7"
                      }
                    />
                  </Flex>
                  <Text>{label}</Text>
                </Flex>
                <AccordionIcon
                  fontSize="18px"
                  color={
                    lightMode ? "var(--chakra-colors-gray-400)" : "#C7C7C7"
                  }
                />
              </Flex>
            </AccordionButton>

            <AccordionPanel padding="0">
              <List px="2px" py="2px">
                {renderItems()}
              </List>
            </AccordionPanel>
          </ListItem>
        </>
      )}
    </>
  );

  return !isSmall ? (
    <AccordionItem border="none">{content}</AccordionItem>
  ) : (
    <div>{content}</div>
  );
};

const mapStateToProps = (state) => {
  return {
    isWidgetVisible: state.application.portal.isWidgetVisible,
    widgets: state.widgets,
    impersonation: state.impersonation,
  };
};
const mapDispatchToProp = (dispatch) => ({
  updateWidgetPropsSaga: (index, props) =>
    dispatch(updateWidgetPropsSaga(index, props)),
  updateWidgetOrderSaga: (oldIndex, newIndex) =>
    dispatch(updateWidgetOrderSaga(oldIndex, newIndex)),
});

export default connect(mapStateToProps, mapDispatchToProp, null, {
  forwardRef: true,
})(SideSubMenu);
